import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, ExternalLink, RefreshCw, ChevronRight, Rss } from "lucide-react";

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  :root{--bg:#0a0a0b;--card:#111113;--border:rgba(255,255,255,0.06);--muted:rgba(255,255,255,0.38);--dim:rgba(255,255,255,0.18)}
  html{scroll-behavior:smooth}
  body{background:var(--bg);color:rgba(255,255,255,0.95);font-family:'Outfit',system-ui,sans-serif;-webkit-font-smoothing:antialiased;min-height:100vh;overflow-x:hidden}
  ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:var(--bg)}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(11px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse-dot{0%,100%{opacity:1;box-shadow:0 0 5px #22c55e}50%{opacity:.35;box-shadow:0 0 1px #22c55e}}
  @keyframes float1{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(-40px,30px) scale(1.08)}66%{transform:translate(30px,-20px) scale(.95)}}
  @keyframes float2{0%,100%{transform:translate(0,0) scale(1)}40%{transform:translate(50px,-40px) scale(1.1)}70%{transform:translate(-20px,30px) scale(.92)}}
  @keyframes float3{0%,100%{transform:translate(0,0) scale(1)}30%{transform:translate(-30px,-30px) scale(1.06)}65%{transform:translate(40px,20px) scale(.96)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
  .fu{animation:fadeUp .42s ease both}.fu1{animation:fadeUp .42s .05s ease both}.fu2{animation:fadeUp .42s .1s ease both}
  .live-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;animation:pulse-dot 2s infinite;flex-shrink:0}
  .skeleton{background:linear-gradient(90deg,rgba(255,255,255,.04)25%,rgba(255,255,255,.07)50%,rgba(255,255,255,.04)75%);background-size:400px 100%;animation:shimmer 1.5s infinite;border-radius:20px}
  .n-card{transition:transform .22s ease,box-shadow .22s ease,background .22s ease,border-color .22s ease}
  .n-card:hover{transform:translateY(-2px)}
  .c-card{transition:transform .24s ease,box-shadow .24s ease}
  .c-card:hover{transform:translateY(-3px) scale(1.012)}
  .tab-btn{font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;letter-spacing:.09em;text-transform:uppercase;cursor:pointer;border:none;outline:none;background:none;color:var(--muted);padding:9px 16px;border-radius:13px;transition:color .18s,background .18s}
  .tab-btn.on{color:#fff;background:rgba(255,255,255,.07)}
  .stt{font-family:'Bebas Neue',sans-serif;letter-spacing:.08em}
  .rj{font-family:'Rajdhani',sans-serif}
  .blue-badge{font-family:'Rajdhani',sans-serif;font-size:9px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;padding:2px 8px;border-radius:6px;background:rgba(50,130,255,.16);border:1px solid rgba(80,150,255,.32);color:#7eb8ff;flex-shrink:0}
  .tag-pill{font-family:'Rajdhani',sans-serif;font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:2px 8px;border-radius:6px;flex-shrink:0}
  @media(max-width:720px){.meta-grid{grid-template-columns:1fr !important}.comm-grid{grid-template-columns:1fr 1fr !important}.role-grid{grid-template-columns:repeat(3,1fr) !important}.dota-pos-grid{grid-template-columns:repeat(3,1fr) !important}.meta-label,.comm-label{display:none !important}}
  @media(max-width:480px){.comm-grid{grid-template-columns:1fr !important}.role-grid{grid-template-columns:1fr !important}.dota-pos-grid{grid-template-columns:repeat(2,1fr) !important}.tab-btn{padding:9px 10px;font-size:11px}}
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const BT_RSS    = "https://www.bluetracker.gg/wow/feed/";
const BT_HOME   = "https://www.bluetracker.gg/wow/";
const CACHE_KEY = "bt_cache_v2";
const REFRESH_MS = 90 * 60 * 1000;

// ─── Time helpers ─────────────────────────────────────────────────────────────
function relTime(date) {
  const s = Math.floor((Date.now() - date) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function clockStr(d) { return d.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }); }
function daysAgo(n)  { return new Date(Date.now() - n * 86400000); }
function hrsAgo(n)   { return new Date(Date.now() - n * 3600000); }

// ─── Static fallback data ─────────────────────────────────────────────────────
// Blue posts: shown while live RSS loads. Timestamps are real so "loading..." never shows.
const BLUE_POSTS = [
  { id:"bp1", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(1),  tags:["Hotfix"],            url:BT_HOME, title:"The War Within – Hotfixes April 13" },
  { id:"bp2", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(2),  tags:["Tuning","Classes"],  url:BT_HOME, title:"Class Tuning – April 11",             excerpt:"Upcoming class balance adjustments across multiple specs ahead of the next raid reset." },
  { id:"bp3", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(3),  tags:["Season"],            url:BT_HOME, title:"Season 2: What to Expect This Week",  excerpt:"Catch up on everything happening in World of Warcraft Season 2 this week." },
  { id:"bp4", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(4),  tags:["PTR","Development"], url:BT_HOME, title:"11.1.5 PTR – Development Notes",       excerpt:"New PTR build adds Mage Tower adjustments, Timerunning scenario tuning, and more." },
  { id:"bp5", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(5),  tags:["Hotfix"],            url:BT_HOME, title:"The War Within – Hotfixes April 8" },
  { id:"bp6", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(6),  tags:["M+"],                url:BT_HOME, title:"Mythic+ Affix Changes – Season 2",     excerpt:"Seasonal affix rotation and dungeon pool adjustments for the upcoming week." },
  { id:"bp7", blue:true, game:"WoW", source:"Blizzard · BlueTracker", _ts:daysAgo(8),  tags:["Event","Community"], url:BT_HOME, title:"Play with the Blues: Mythic Undermine(d)" },
];
BLUE_POSTS.forEach(p => { p.time = relTime(p._ts); });

const WOW_ARTICLES = [
  { id:"w1", blue:false, game:"WoW", source:"Ruse's Bakery", _ts:daysAgo(1), tags:["M+","Builds"], featured:true, url:null,
    title:"Season 2 Mythic+ Tier List — Every Spec Ranked",
    excerpt:"Season 2 is well underway. Our community tier list is updated with the latest simulation data and high-key push results.",
    grad:"linear-gradient(135deg,#10193a 0%,#0d1f3c 60%,#0a0a0b 100%)" },
  { id:"w2", blue:false, game:"WoW", source:"Wowhead",      _ts:hrsAgo(6),  tags:["Gear","BiS"],
    url:"https://www.wowhead.com/news",
    title:"Best in Slot Gear Guide – Season 2 Week 3",
    excerpt:"Complete BiS lists for all specs after the third week of Season 2 progression." },
  { id:"w3", blue:false, game:"WoW", source:"MMO-Champion", _ts:hrsAgo(14), tags:["Raid","Guide"],
    url:"https://www.mmo-champion.com",
    title:"Liberation of Undermine Mythic Race — Current Standings" },
  { id:"w4", blue:false, game:"WoW", source:"Wowhead",      _ts:daysAgo(2), tags:["PTR"],
    url:"https://www.wowhead.com/news",
    title:"Patch 11.1.5 PTR: New Timerunning Scenario, Class Changes & More" },
  { id:"w5", blue:false, game:"WoW", source:"MMO-Champion", _ts:daysAgo(3), tags:["Tuning","Classes"],
    url:"https://www.mmo-champion.com",
    title:"Season 2 Week 3 Tuning: Feral Druid & Unholy DK Buffed" },
];
WOW_ARTICLES.forEach(a => { a.time = relTime(a._ts); });

// Dota 2 — real patch list as of April 2025 (latest: 7.41b)
const DOTA_ARTICLES = [
  { id:"d1", blue:false, game:"Dota 2", source:"Dota 2 Official", _ts:daysAgo(4), tags:["Patch Notes"], featured:true,
    url:"https://www.dota2.com/patches/7.41b",
    title:"Patch 7.41b – Balance Update",
    excerpt:"Follow-up balance update targeting Ringmaster, Kez, and several dominant carry picks from the 7.41 meta.",
    grad:"linear-gradient(135deg,#1a0808 0%,#2d0d0d 60%,#0a0a0b 100%)" },
  { id:"d2", blue:false, game:"Dota 2", source:"Dota 2 Official", _ts:daysAgo(18), tags:["Patch Notes"],
    url:"https://www.dota2.com/patches/7.41",
    title:"Patch 7.41 – New Hero: Ringmaster" },
  { id:"d3", blue:false, game:"Dota 2", source:"Dota 2 Official", _ts:daysAgo(35), tags:["Patch Notes"],
    url:"https://www.dota2.com/patches/7.40b",
    title:"Patch 7.40b – Balance Update" },
  { id:"d4", blue:false, game:"Dota 2", source:"Dota 2 Official", _ts:daysAgo(55), tags:["Patch Notes"],
    url:"https://www.dota2.com/patches/7.40",
    title:"Patch 7.40 – The Crownfall Continues" },
  { id:"d5", blue:false, game:"Dota 2", source:"Dota 2 Official", _ts:daysAgo(12), tags:["Esports"],
    url:"https://www.dota2.com/esports",
    title:"DreamLeague Season 25 – Group Stage Results" },
];
DOTA_ARTICLES.forEach(a => { a.time = relTime(a._ts); });

// ─── Tag colours ──────────────────────────────────────────────────────────────
const TC = {
  "Tuning":["rgba(245,158,11,.12)","#fbbf24","rgba(245,158,11,.22)"],
  "Classes":["rgba(139,92,246,.12)","#a78bfa","rgba(139,92,246,.22)"],
  "Items":["rgba(255,128,0,.12)","#fb923c","rgba(255,128,0,.22)"],
  "Crafting":["rgba(255,128,0,.10)","#fb923c","rgba(255,128,0,.18)"],
  "Event":["rgba(236,72,153,.12)","#f472b6","rgba(236,72,153,.22)"],
  "Community":["rgba(34,211,238,.10)","#22d3ee","rgba(34,211,238,.18)"],
  "Weekly":["rgba(34,197,94,.10)","#4ade80","rgba(34,197,94,.18)"],
  "Hotfix":["rgba(239,68,68,.12)","#f87171","rgba(239,68,68,.22)"],
  "PTR":["rgba(251,146,60,.12)","#fb923c","rgba(251,146,60,.22)"],
  "Development":["rgba(96,165,250,.10)","#60a5fa","rgba(96,165,250,.18)"],
  "M+":["rgba(16,185,129,.12)","#34d399","rgba(16,185,129,.22)"],
  "Builds":["rgba(16,185,129,.10)","#34d399","rgba(16,185,129,.18)"],
  "Gear":["rgba(139,92,246,.12)","#a78bfa","rgba(139,92,246,.22)"],
  "BiS":["rgba(139,92,246,.10)","#a78bfa","rgba(139,92,246,.18)"],
  "Raid":["rgba(239,68,68,.12)","#f87171","rgba(239,68,68,.22)"],
  "Guide":["rgba(6,182,212,.12)","#22d3ee","rgba(6,182,212,.22)"],
  "Evoker":["rgba(139,92,246,.12)","#c4b5fd","rgba(139,92,246,.22)"],
  "Patch Notes":["rgba(245,158,11,.12)","#fbbf24","rgba(245,158,11,.22)"],
  "Esports":["rgba(236,72,153,.12)","#f472b6","rgba(236,72,153,.22)"],
  "TI 2025":["rgba(239,68,68,.12)","#f87171","rgba(239,68,68,.22)"],
  "Hero":["rgba(194,60,42,.12)","#f87171","rgba(194,60,42,.22)"],
  "Season":["rgba(245,158,11,.12)","#fbbf24","rgba(245,158,11,.22)"],
  "News":["rgba(255,255,255,.05)","rgba(255,255,255,.4)","rgba(255,255,255,.1)"],
};
function TagPill({ tag }) {
  const [bg,color,border]=TC[tag]??TC["News"];
  return <span className="tag-pill" style={{background:bg,color,border:`1px solid ${border}`}}>{tag}</span>;
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const DiscordIcon = ({ size=20, color="#b9bdff" }) => (
  <svg width={size} height={size*.76} viewBox="0 0 127.14 96.36" fill={color}>
    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
  </svg>
);

const BlizzardWM = () => (
  <div style={{position:"absolute",right:-8,top:"50%",transform:"translateY(-50%)",width:80,height:80,opacity:.055,pointerEvents:"none"}}>
    <svg viewBox="0 0 100 100" fill="none">
      <circle cx="50" cy="50" r="46" stroke="white" strokeWidth="4"/>
      <circle cx="50" cy="50" r="30" stroke="white" strokeWidth="3"/>
      <path d="M50 20 L56 38 L76 38 L61 50 L67 68 L50 57 L33 68 L39 50 L24 38 L44 38 Z" fill="white"/>
    </svg>
  </div>
);

const GameDot = ({ game }) => {
  const col = game==="WoW" ? "#3b82f6" : game==="Dota 2" ? "#ef4444" : "#a78bfa";
  return <div style={{width:8,height:8,borderRadius:"50%",background:col,boxShadow:`0 0 4px ${col}`,flexShrink:0}}/>;
};

// ─── News Card ────────────────────────────────────────────────────────────────
function NewsCard({ article, index }) {
  const [hv,setHv]=useState(false);
  const {blue:isBlue,featured:isFeat}=article;
  const cardBg=isBlue?(hv?"rgba(25,85,200,.1)":"rgba(18,65,160,.05)"):(hv?"#18181b":"#111113");
  const border=isBlue?(hv?"rgba(80,150,255,.48)":"rgba(50,130,255,.22)"):(hv?"rgba(255,255,255,.1)":"rgba(255,255,255,.06)");
  const shadow=hv?(isBlue?"0 8px 32px rgba(30,100,255,.13)":"0 8px 28px rgba(0,0,0,.45)"):"none";
  const titleClr=hv?(isBlue?"#7eb8ff":article.game==="Dota 2"?"#f87171":"#fbbf24"):"rgba(255,255,255,.93)";
  const isExt=article.url&&article.url.startsWith("http");
  const liveTime=article._ts?relTime(article._ts):article.time;

  return (
    <a href={isExt?article.url:undefined} target={isExt?"_blank":undefined}
       rel={isExt?"noopener noreferrer":undefined}
       style={{textDecoration:"none",display:"block",animation:`fadeUp .4s ${index*45}ms ease both`,opacity:0,cursor:isExt?"pointer":"default"}}
       onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)} tabIndex={isExt?0:-1}>
      <div className="n-card" style={{position:"relative",overflow:"hidden",borderRadius:20,border:`1px solid ${border}`,background:cardBg,boxShadow:shadow,padding:isFeat?0:"14px 16px"}}>
        {isBlue&&<BlizzardWM/>}
        {isFeat&&(
          <>
            <div style={{height:80,background:article.grad,position:"relative"}}>
              <div style={{position:"absolute",right:20,top:"50%",transform:"translateY(-50%)",width:55,height:55,borderRadius:"50%",background:"radial-gradient(circle,rgba(255,255,255,.07) 0%,transparent 70%)",border:"1px solid rgba(255,255,255,.07)"}}/>
            </div>
            <div style={{padding:"12px 16px 14px"}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8,flexWrap:"wrap"}}>
                <GameDot game={article.game}/>
                {article.tags?.map(t=><TagPill key={t} tag={t}/>)}
                <span style={{marginLeft:"auto",fontSize:11,color:"var(--dim)",fontFamily:"'Rajdhani',sans-serif",fontWeight:600}}>{liveTime}</span>
              </div>
              <h3 style={{fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:15,lineHeight:1.25,color:titleClr,transition:"color .2s",marginBottom:6}}>{article.title}</h3>
              {article.excerpt&&<p style={{fontSize:12,color:"var(--muted)",lineHeight:1.55,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{article.excerpt}</p>}
            </div>
          </>
        )}
        {!isFeat&&(
          <>
            <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:7,flexWrap:"wrap"}}>
              <GameDot game={article.game}/>
              {isBlue&&<span className="blue-badge">BLUE POST</span>}
              {article.tags?.map(t=><TagPill key={t} tag={t}/>)}
              <span style={{marginLeft:"auto",fontSize:11,color:"var(--dim)",fontFamily:"'Rajdhani',sans-serif",fontWeight:600,flexShrink:0}}>{liveTime}</span>
            </div>
            <h3 style={{fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:14.5,lineHeight:1.25,color:titleClr,transition:"color .2s"}}>{article.title}</h3>
            {article.excerpt&&<p style={{fontSize:12,color:"var(--muted)",lineHeight:1.55,marginTop:5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{article.excerpt}</p>}
            {article.itemLinks?.map(item=>(
              <a key={item.id} href={`https://www.wowhead.com/item=${item.id}`} data-wowhead={`item=${item.id}`}
                 target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                 style={{display:"inline-block",marginTop:5,fontSize:11,color:"rgba(255,128,0,.65)",textDecoration:"underline",textDecorationColor:"rgba(255,128,0,.25)"}}>
                {item.name}
              </a>
            ))}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8}}>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <span className="rj" style={{fontSize:11,color:"var(--dim)",fontWeight:600,letterSpacing:".06em",textTransform:"uppercase"}}>{article.source}</span>
                {isBlue&&<span style={{fontSize:9,color:"rgba(80,150,255,.45)",fontFamily:"'Rajdhani',sans-serif",fontWeight:600}}>· bluetracker.gg</span>}
              </div>
              {isExt&&(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span className="rj" style={{fontSize:10,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:hv?(isBlue?"#7eb8ff":article.game==="Dota 2"?"#f87171":"#fbbf24"):"var(--dim)",transition:"color .2s"}}>Read</span>
                  <ChevronRight size={13} style={{color:hv?(isBlue?"#7eb8ff":article.game==="Dota 2"?"#f87171":"#fbbf24"):"var(--dim)",transition:"color .2s,transform .2s",transform:hv?"translateX(2px)":"translateX(0)"}}/>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </a>
  );
}

// ─── Community Cards ──────────────────────────────────────────────────────────
const COMM=[
  {id:"discord",label:"DISCORD",     url:"https://discord.gg/3yKHBx3JZ",  acc:"#5865F2",desc:"Join the Ruse's Bakery community. Discuss meta, share builds, get exclusive updates."},
  {id:"wowhead",label:"WOWHEAD",     url:"https://www.wowhead.com",        acc:"#ff8000",desc:"The largest WoW database. Items, quests, guides, talent calculators, and news."},
  {id:"mmo",    label:"MMO-CHAMPION",url:"https://www.mmo-champion.com",   acc:"#4da6ff",desc:"Breaking WoW news, patch notes, datamining, and top-tier community forums."},
  {id:"dota2",  label:"DOTA 2 NEWS", url:"https://www.dota2.com/news",     acc:"#c23c2a",desc:"Official patch notes, hero updates, esports coverage, and The International news."},
];
const COMM_ICONS={
  discord:({acc})=><DiscordIcon size={22} color={acc}/>,
  wowhead:()=><svg width={22} height={22} viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="rgba(255,128,0,.12)" stroke="rgba(255,128,0,.3)" strokeWidth="2"/><path d="M50 15 L62 38 L88 38 L67 55 L75 80 L50 65 L25 80 L33 55 L12 38 L38 38 Z" fill="#ff8000" opacity=".9"/></svg>,
  mmo:    ()=><svg width={22} height={22} viewBox="0 0 100 100"><rect width="100" height="100" rx="18" fill="rgba(77,166,255,.12)" stroke="rgba(77,166,255,.3)" strokeWidth="2"/><path d="M20 30h60M20 50h60M20 70h40" stroke="#4da6ff" strokeWidth="8" strokeLinecap="round"/><circle cx="75" cy="70" r="10" fill="#4da6ff"/></svg>,
  dota2:  ()=><svg width={22} height={22} viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="rgba(194,60,42,.12)" stroke="rgba(194,60,42,.3)" strokeWidth="2"/><text x="50" y="67" textAnchor="middle" fontSize="52" fontWeight="900" fill="#c23c2a" fontFamily="serif">D</text></svg>,
};
function CommCard({card,index}){
  const [hv,setHv]=useState(false);
  const a=card.acc;
  const IconComp=COMM_ICONS[card.id];
  return(
    <a href={card.url} target="_blank" rel="noopener noreferrer"
       style={{textDecoration:"none",display:"block",animation:`fadeUp .42s ${0.25+index*.07}s ease both`,opacity:0}}
       onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>
      <div className="c-card" style={{borderRadius:22,height:"100%",cursor:"pointer",border:`1px solid ${hv?a+"66":a+"28"}`,background:hv?`linear-gradient(135deg,${a}20 0%,${a}05 100%)`:`linear-gradient(135deg,${a}10 0%,${a}02 100%)`,boxShadow:hv?`0 12px 36px ${a}28`:"none",padding:"22px 20px",position:"relative",overflow:"hidden",display:"flex",flexDirection:"column",gap:12}}>
        <div style={{position:"absolute",top:-28,right:-28,width:90,height:90,borderRadius:"50%",background:`radial-gradient(circle,${a}18 0%,transparent 70%)`,opacity:hv?1:.35,transition:"opacity .3s",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:46,height:46,borderRadius:14,background:`${a}18`,border:`1px solid ${a}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><IconComp acc={a}/></div>
          <div className="stt" style={{fontSize:18,color:hv?"#fff":"rgba(255,255,255,.88)",transition:"color .2s"}}>{card.label}</div>
          <ExternalLink size={13} style={{marginLeft:"auto",color:hv?a:"var(--dim)",transition:"color .2s"}}/>
        </div>
        <p style={{fontSize:12,color:"var(--muted)",lineHeight:1.6}}>{card.desc}</p>
        <div className="rj" style={{marginTop:"auto",display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:hv?a:"var(--dim)",transition:"color .2s"}}>
          Visit <ChevronRight size={11} style={{transform:hv?"translateX(3px)":"translateX(0)",transition:"transform .2s"}}/>
        </div>
      </div>
    </a>
  );
}

// ─── Role Capsules ────────────────────────────────────────────────────────────
const ROLES=[
  {label:"DPS",   url:"https://murlok.io/meta/dps/m+",    col:"#f97316",glow:"rgba(249,115,22,.30)",border:"rgba(249,115,22,.55)",icon:"https://render.worldofwarcraft.com/us/icons/56/ability_backstab.jpg"},
  {label:"HEALER",url:"https://murlok.io/meta/healer/m+", col:"#22c55e",glow:"rgba(34,197,94,.28)", border:"rgba(34,197,94,.55)", icon:"https://render.worldofwarcraft.com/us/icons/56/spell_holy_flashheal.jpg"},
  {label:"TANK",  url:"https://murlok.io/meta/tank/m+",   col:"#3b82f6",glow:"rgba(59,130,246,.28)",border:"rgba(59,130,246,.55)",icon:"https://render.worldofwarcraft.com/us/icons/56/ability_defend.jpg"},
];
function RoleCapsule({role}){
  const [hv,setHv]=useState(false);
  return(
    <a href={role.url} target="_blank" rel="noopener noreferrer" style={{textDecoration:"none",display:"block"}}
       onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 20px 10px 10px",borderRadius:50,border:`1px solid ${hv?role.border:role.col+"30"}`,background:hv?"rgba(0,0,0,.82)":"rgba(0,0,0,.70)",backdropFilter:"blur(10px)",boxShadow:hv?`0 0 0 1px ${role.border},0 8px 28px ${role.glow}`:`0 0 0 1px ${role.col}18`,cursor:"pointer",transform:hv?"scale(1.05)":"scale(1)",transition:"transform .22s ease,box-shadow .22s ease,border-color .22s ease,background .22s ease",userSelect:"none"}}>
        <div style={{width:42,height:42,borderRadius:50,border:`2px solid ${hv?role.col:role.col+"60"}`,overflow:"hidden",flexShrink:0,boxShadow:hv?`0 0 10px ${role.glow}`:"none",transition:"border-color .22s,box-shadow .22s"}}>
          <img src={role.icon} alt={role.label} width={42} height={42} style={{display:"block",width:"100%",height:"100%",objectFit:"cover"}}/>
        </div>
        <span className="stt" style={{fontSize:22,letterSpacing:".1em",color:hv?"#fff":"rgba(255,255,255,.82)",transition:"color .2s",lineHeight:1}}>{role.label}</span>
        <span style={{marginLeft:"auto",fontSize:16,color:role.col,opacity:hv?1:0,transform:hv?"translateX(0)":"translateX(-4px)",transition:"opacity .2s,transform .2s"}}>→</span>
      </div>
    </a>
  );
}
function MetaRankings(){
  return(
    <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:20,alignItems:"center",marginBottom:28}} className="meta-grid fu2">
      <div className="meta-label">
        <div className="stt" style={{fontSize:26,color:"#fff",marginBottom:8}}>META RANKINGS</div>
        <p style={{fontSize:12,color:"var(--muted)",lineHeight:1.65}}>Explore the meta and discover how each role ranks. Powered by <span style={{color:"rgba(255,255,255,.4)"}}>murlok.io</span>.</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}} className="role-grid">
        {ROLES.map(r=><RoleCapsule key={r.label} role={r}/>)}
      </div>
    </div>
  );
}

// ─── Dota 2 Meta ──────────────────────────────────────────────────────────────

const DOTA_POSITIONS = [
  { pos:1, label:"CARRY",        col:"#f59e0b", glow:"rgba(245,158,11,.30)", border:"rgba(245,158,11,.55)" },
  { pos:2, label:"MID",          col:"#a855f7", glow:"rgba(168,85,247,.28)", border:"rgba(168,85,247,.55)" },
  { pos:3, label:"OFFLANE",      col:"#ef4444", glow:"rgba(239,68,68,.28)",  border:"rgba(239,68,68,.55)"  },
  { pos:4, label:"SUPPORT",      col:"#22c55e", glow:"rgba(34,197,94,.28)",  border:"rgba(34,197,94,.55)"  },
  { pos:5, label:"HARD SUPPORT", col:"#3b82f6", glow:"rgba(59,130,246,.28)", border:"rgba(59,130,246,.55)" },
];

function DotaPositionCapsule({ pos }) {
  const [hv, setHv] = useState(false);
  return (
    <a href="https://dota2protracker.com/meta" target="_blank" rel="noopener noreferrer"
       style={{ textDecoration:"none", display:"block" }}
       onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>
      <div style={{
        display:"flex", alignItems:"center", gap:10,
        padding:"10px 16px 10px 10px", borderRadius:50,
        border:`1px solid ${hv ? pos.border : pos.col+"30"}`,
        background: hv ? "rgba(0,0,0,.82)" : "rgba(0,0,0,.70)",
        backdropFilter:"blur(10px)",
        boxShadow: hv
          ? `0 0 0 1px ${pos.border}, 0 8px 28px ${pos.glow}`
          : `0 0 0 1px ${pos.col}18`,
        cursor:"pointer",
        transform: hv ? "scale(1.05)" : "scale(1)",
        transition:"transform .22s ease, box-shadow .22s ease, border-color .22s ease, background .22s ease",
        userSelect:"none",
      }}>
        {/* Position number badge */}
        <div style={{
          width:42, height:42, borderRadius:50, flexShrink:0,
          background:`${pos.col}20`,
          border:`2px solid ${hv ? pos.col : pos.col+"55"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow: hv ? `0 0 12px ${pos.glow}` : "none",
          transition:"border-color .22s, box-shadow .22s",
        }}>
          <span className="stt" style={{
            fontSize:20, lineHeight:1,
            color: hv ? pos.col : pos.col+"cc",
            transition:"color .22s",
          }}>
            {pos.pos}
          </span>
        </div>

        {/* Label */}
        <span className="stt" style={{
          fontSize:pos.label.length > 7 ? 13 : 17,
          letterSpacing:".07em",
          color: hv ? "#fff" : "rgba(255,255,255,.82)",
          transition:"color .2s", lineHeight:1,
        }}>
          {pos.label}
        </span>

        {/* Arrow */}
        <span style={{
          marginLeft:"auto", fontSize:14, color:pos.col,
          opacity: hv ? 1 : 0,
          transform: hv ? "translateX(0)" : "translateX(-4px)",
          transition:"opacity .2s, transform .2s",
        }}>→</span>
      </div>
    </a>
  );
}

function DotaMeta() {
  return (
    <div style={{
      display:"grid", gridTemplateColumns:"200px 1fr",
      gap:20, alignItems:"center", marginBottom:36,
    }} className="meta-grid fu2">

      {/* Left description */}
      <div className="meta-label">
        {/* Dota 2 "D" logo */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
          <div style={{
            width:32, height:32, borderRadius:8,
            background:"rgba(194,60,42,.2)", border:"1px solid rgba(194,60,42,.35)",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>
            <svg width={18} height={18} viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" fill="rgba(194,60,42,.15)" stroke="rgba(194,60,42,.5)" strokeWidth="3"/>
              <text x="50" y="67" textAnchor="middle" fontSize="52" fontWeight="900" fill="#ef4444" fontFamily="serif">D</text>
            </svg>
          </div>
          <div className="stt" style={{ fontSize:26, color:"#ef4444", lineHeight:1 }}>DOTA 2 META</div>
        </div>
        <p style={{ fontSize:12, color:"var(--muted)", lineHeight:1.65 }}>
          Patch 7.41b hero meta by position. Win rates, pick rates & top builds from high-MMR matches.
        </p>
        <a href="https://dota2protracker.com/meta" target="_blank" rel="noopener noreferrer"
           style={{ display:"inline-block", marginTop:8, fontSize:10,
             color:"rgba(239,68,68,.55)", fontFamily:"'Rajdhani',sans-serif",
             fontWeight:600, letterSpacing:".06em", textDecoration:"none",
             transition:"color .2s" }}
           onMouseEnter={e=>e.currentTarget.style.color="rgba(239,68,68,.9)"}
           onMouseLeave={e=>e.currentTarget.style.color="rgba(239,68,68,.55)"}>
          dota2protracker.com ↗
        </a>
      </div>

      {/* 5 position capsules */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}
           className="dota-pos-grid">
        {DOTA_POSITIONS.map(p => <DotaPositionCapsule key={p.pos} pos={p} />)}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────
function Header({scrolled}){
  return(
    <header style={{position:"sticky",top:0,zIndex:100,background:scrolled?"rgba(10,10,11,.92)":"transparent",backdropFilter:scrolled?"blur(20px)":"none",borderBottom:scrolled?"1px solid var(--border)":"1px solid transparent",transition:"all .3s"}}>
      <div style={{maxWidth:900,margin:"0 auto",padding:"0 20px",display:"flex",alignItems:"center",height:60,gap:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <div style={{width:34,height:34,background:"linear-gradient(135deg,#f59e0b,#a78bfa)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Rss size={16} color="#fff"/>
          </div>
          <div>
            <div className="stt" style={{fontSize:16,color:"rgba(255,255,255,.95)",lineHeight:1}}>RUSE'S BAKERY</div>
            <div className="rj" style={{fontSize:9,color:"var(--dim)",letterSpacing:".12em",fontWeight:600}}>GAMING NEWS</div>
          </div>
        </div>
        <div style={{flex:1}}/>
        <a href="https://discord.gg/3yKHBx3JZ" target="_blank" rel="noopener noreferrer"
           style={{display:"flex",alignItems:"center",gap:8,padding:"8px 16px",borderRadius:12,border:"1px solid rgba(88,101,242,.35)",background:"rgba(88,101,242,.12)",color:"rgba(185,189,255,.9)",fontSize:12,fontFamily:"'Rajdhani',sans-serif",fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",textDecoration:"none",transition:"all .2s"}}
           onMouseEnter={e=>{e.currentTarget.style.background="rgba(88,101,242,.22)";e.currentTarget.style.borderColor="rgba(88,101,242,.55)"}}
           onMouseLeave={e=>{e.currentTarget.style.background="rgba(88,101,242,.12)";e.currentTarget.style.borderColor="rgba(88,101,242,.35)"}}>
          <DiscordIcon size={14} color="#b9bdff"/> Discord
        </a>
      </div>
    </header>
  );
}

// ─── RSS fetch — PARALLEL with Promise.any ────────────────────────────────────
//
//  All proxies race simultaneously. First to return valid data wins.
//  Total wait = slowest-of-the-fastest, not sum of all timeouts.
//  localStorage cache used as instant fallback if all proxies fail.
//
function tagsFromTitle(t=""){
  const l=t.toLowerCase();
  if(l.includes("hotfix"))    return ["Hotfix"];
  if(l.includes("tuning"))    return ["Tuning","Classes"];
  if(l.includes("ptr"))       return ["PTR","Development"];
  if(l.includes("weekly"))    return ["Weekly","News"];
  if(l.includes("play with")) return ["Event","Community"];
  if(l.includes("recraft"))   return ["Items","Crafting"];
  if(l.includes("season"))    return ["Season"];
  return ["News"];
}
function shapeItem(i,{title="",link="",pubDate="",description=""}){
  const parsed=pubDate?new Date(pubDate):new Date();
  const excerpt=description.replace(/<[^>]+>/g,"").replace(/&[a-z#0-9]+;/gi," ").replace(/\s+/g," ").trim().slice(0,220)||undefined;
  return{id:`bt-${i}`,blue:true,game:"WoW",source:"Blizzard · BlueTracker",time:relTime(parsed),_ts:parsed,url:(link&&link.startsWith("http"))?link:BT_HOME,tags:tagsFromTitle(title),title:title.trim(),excerpt};
}
function parseRSSXml(text){
  try{
    const xml=new DOMParser().parseFromString(text,"text/xml");
    if(xml.querySelector("parsererror")) return null;
    const items=Array.from(xml.querySelectorAll("item")).slice(0,30);
    if(!items.length) return null;
    return items.map((el,i)=>{
      const linkEl=el.querySelector("link");
      const rawLink=linkEl?.nextSibling?.nodeValue?.trim()||linkEl?.textContent?.trim()||"";
      return shapeItem(i,{title:el.querySelector("title")?.textContent??"",link:rawLink,pubDate:el.querySelector("pubDate")?.textContent??"",description:el.querySelector("description")?.textContent??""});
    });
  }catch{return null;}
}
function readCache(){
  try{const{items}=JSON.parse(localStorage.getItem(CACHE_KEY)||"{}");return items?.map(a=>({...a,_ts:new Date(a._ts)}));}
  catch{return null;}
}
function writeCache(items){
  try{localStorage.setItem(CACHE_KEY,JSON.stringify({ts:Date.now(),items}));}catch{}
}

// Each proxy attempt returns a Promise that resolves to a result array or rejects
function tryRaw(enc){
  return fetch(`https://api.allorigins.win/raw?url=${enc}`)
    .then(r=>{ if(!r.ok) throw new Error("not ok"); return r.text(); })
    .then(t=>{ const r=parseRSSXml(t); if(!r?.length) throw new Error("empty"); return r; });
}
function tryRss2json(enc){
  return fetch(`https://api.rss2json.com/v1/api.json?rss_url=${enc}&count=30`)
    .then(r=>{ if(!r.ok) throw new Error("not ok"); return r.json(); })
    .then(j=>{ if(j.status!=="ok"||!j.items?.length) throw new Error("empty"); return j.items.map((it,i)=>shapeItem(i,{title:it.title??"",link:it.link??"",pubDate:it.pubDate??"",description:it.description??it.content??""})); });
}
function tryCorsproxy(enc){
  return fetch(`https://corsproxy.io/?${enc}`)
    .then(r=>{ if(!r.ok) throw new Error("not ok"); return r.text(); })
    .then(t=>{ const r=parseRSSXml(t); if(!r?.length) throw new Error("empty"); return r; });
}

async function fetchBlueTracker(){
  const enc=encodeURIComponent(BT_RSS);
  // Race all three proxies in parallel — first success wins
  try{
    const result=await Promise.any([
      tryRaw(enc),
      tryRss2json(enc),
      tryCorsproxy(enc),
    ]);
    writeCache(result);
    return result;
  }catch{
    // All three failed — use localStorage cache
    return readCache()||null;
  }
}

// ─── News Section ─────────────────────────────────────────────────────────────
const TABS=[{id:"all",label:"ALL NEWS"},{id:"wow",label:"WOW"},{id:"dota",label:"DOTA 2"},{id:"blue",label:"BLUE POSTS"}];

function NewsSection({tab,setTab}){
  const [livePosts,   setLivePosts]   = useState(null);
  const [fetching,    setFetching]    = useState(false);
  const [fetchError,  setFetchError]  = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [tick,        setTick]        = useState(0);

  const doFetch=useCallback(async(spinner=true)=>{
    if(spinner) setFetching(true);
    setFetchError(false);
    const posts=await fetchBlueTracker();
    if(posts?.length){setLivePosts(posts);setFetchError(false);}
    else setFetchError(true);
    setLastFetched(new Date());
    setTick(0);
    setFetching(false);
  },[]);

  useEffect(()=>{doFetch(true);const iv=setInterval(()=>doFetch(false),REFRESH_MS);return()=>clearInterval(iv);},[doFetch]);
  useEffect(()=>{const iv=setInterval(()=>setTick(t=>t+1),1000);return()=>clearInterval(iv);},[]);

  const ageLabel=lastFetched?relTime(lastFetched).toUpperCase():"LOADING";
  const nextSec=lastFetched?Math.max(0,Math.round(REFRESH_MS/1000-tick)):null;
  const nextLabel=nextSec!==null?(nextSec>60?`next in ${Math.ceil(nextSec/60)}m`:`next in ${nextSec}s`):"";

  const bluePosts=(livePosts??BLUE_POSTS).map(a=>({...a,time:a._ts?relTime(a._ts):a.time}));
  const allArts=[...bluePosts,...WOW_ARTICLES,...DOTA_ARTICLES];
  const articles=allArts.filter(a=>
    tab==="all"||
    (tab==="wow" &&a.game==="WoW")||
    (tab==="dota"&&a.game==="Dota 2")||
    (tab==="blue"&&a.blue)
  );

  return(
    <section style={{marginBottom:52}}>
      {/* Header */}
      <div className="fu" style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:28}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:10}}>
          <Bell size={30} color="#fff" strokeWidth={1.5}/>
          <span className="stt" style={{fontSize:34,color:"#fff",letterSpacing:".1em"}}>NEWS</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
          <div className="live-dot"/>
          <span className="rj" style={{fontSize:11,color:"var(--muted)",fontWeight:600,letterSpacing:".06em"}}>LIVE FEED · UPDATED {ageLabel}</span>
          {nextLabel&&<span className="rj" style={{fontSize:10,color:"var(--dim)",fontWeight:600}}>· {nextLabel}</span>}
          {livePosts&&!fetchError&&<span style={{fontSize:9,padding:"2px 8px",borderRadius:6,background:"rgba(34,197,94,.1)",border:"1px solid rgba(34,197,94,.2)",color:"#4ade80",fontFamily:"'Rajdhani',sans-serif",fontWeight:700,letterSpacing:".1em"}}>LIVE · BLUETRACKER</span>}
          {fetchError&&(
            <a href={BT_HOME} target="_blank" rel="noopener noreferrer"
               style={{fontSize:9,padding:"2px 10px",borderRadius:6,background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.18)",color:"#f87171",fontFamily:"'Rajdhani',sans-serif",fontWeight:700,letterSpacing:".08em",textDecoration:"none"}}>
              ⚠ RSS OFFLINE — VIEW ON BLUETRACKER.GG
            </a>
          )}
          <button onClick={()=>doFetch(true)} disabled={fetching}
            style={{background:"none",border:"none",cursor:fetching?"default":"pointer",color:"var(--dim)",display:"flex",alignItems:"center",gap:4,fontSize:11,fontFamily:"'Rajdhani',sans-serif",fontWeight:600,letterSpacing:".06em",padding:"3px 8px",borderRadius:8,transition:"color .2s,background .2s",opacity:fetching?.5:1}}
            onMouseEnter={e=>{if(!fetching){e.currentTarget.style.color="#fff";e.currentTarget.style.background="rgba(255,255,255,.05)"}}}
            onMouseLeave={e=>{e.currentTarget.style.color="var(--dim)";e.currentTarget.style.background="none"}}>
            <RefreshCw size={11} style={{animation:fetching?"spin .8s linear infinite":"none"}}/> REFRESH
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="fu1" style={{display:"flex",gap:4,background:"#111113",border:"1px solid var(--border)",borderRadius:17,padding:5,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} className={`tab-btn${tab===t.id?" on":""}`} onClick={()=>setTab(t.id)} style={{flex:1}}>
            {t.label}
            {t.id==="blue"&&livePosts&&<span style={{marginLeft:5,fontSize:9,padding:"1px 6px",borderRadius:5,background:"rgba(34,197,94,.12)",border:"1px solid rgba(34,197,94,.22)",color:"#4ade80",verticalAlign:"middle",fontWeight:700}}>{livePosts.length}</span>}
          </button>
        ))}
      </div>
      {/* Cards */}
      {fetching&&livePosts===null?(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[1,2,3,4,5,6,7].map(i=><div key={i} className="skeleton" style={{height:76,animationDelay:`${i*80}ms`}}/>)}
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {articles.map((a,i)=><NewsCard key={a.id} article={a} index={i}/>)}
          {articles.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:"var(--dim)",fontFamily:"'Rajdhani',sans-serif",fontSize:13,letterSpacing:".06em"}}>NO ARTICLES IN THIS CATEGORY</div>}
        </div>
      )}
      {lastFetched&&(
        <div style={{marginTop:16,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <span className="rj" style={{fontSize:10,color:"var(--dim)",fontWeight:600,letterSpacing:".04em"}}>Last synced {clockStr(lastFetched)} · auto-refreshes every 90 min</span>
          <a href={BT_HOME} target="_blank" rel="noopener noreferrer"
             style={{fontSize:10,color:"rgba(80,150,255,.45)",fontFamily:"'Rajdhani',sans-serif",fontWeight:600,textDecoration:"none",letterSpacing:".04em",transition:"color .2s"}}
             onMouseEnter={e=>e.currentTarget.style.color="rgba(125,185,255,.8)"} onMouseLeave={e=>e.currentTarget.style.color="rgba(80,150,255,.45)"}>
            bluetracker.gg ↗
          </a>
        </div>
      )}
    </section>
  );
}

// ─── Community Section ────────────────────────────────────────────────────────
function CommunitySection(){
  return(
    <section style={{marginBottom:52}}>
      <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:24,alignItems:"start"}} className="comm-grid">
        <div className="comm-label fu2">
          <div className="stt" style={{fontSize:26,color:"#fff",marginBottom:8}}>COMMUNITY PORTALS</div>
          <p style={{fontSize:12,color:"var(--muted)",lineHeight:1.65}}>Stay connected with the Ruse's Bakery community. Join our Discord and explore premier news portals.</p>
          <p style={{fontSize:12,color:"var(--dim)",lineHeight:1.6,marginTop:8}}>Share strategies, discuss updates, and celebrate victories together.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {COMM.map((c,i)=><CommCard key={c.id} card={c} index={i}/>)}
        </div>
      </div>
    </section>
  );
}

// ─── Guides Section ──────────────────────────────────────────────────────────
// Full murlok.io class/spec grid — every WoW class with spec pills + mode switcher

const ICON = "https://render.worldofwarcraft.com/us/icons/56/";
const ART  = "https://static.murlok.io/web/art/og/";

const CLASSES = [
  { name:"Death Knight",  slug:"death-knight",   color:"#C41E3A", icon:"spell_deathknight_classicon.jpg",
    specs:[{n:"Blood",      s:"blood",        i:"spell_deathknight_bloodpresence.jpg"},
           {n:"Frost",      s:"frost",        i:"spell_deathknight_frostpresence.jpg"},
           {n:"Unholy",     s:"unholy",       i:"spell_deathknight_unholypresence.jpg"}] },
  { name:"Demon Hunter",  slug:"demon-hunter",   color:"#A330C9", icon:"classicon_demonhunter.jpg",
    specs:[{n:"Havoc",      s:"havoc",        i:"ability_demonhunter_specdps.jpg"},
           {n:"Vengeance",  s:"vengeance",    i:"ability_demonhunter_spectank.jpg"},
           {n:"Devourer",   s:"devourer",     i:"classicon_demonhunter_void_64.jpg"}] },
  { name:"Druid",         slug:"druid",          color:"#FF7C0A", icon:"classicon_druid.jpg",
    specs:[{n:"Balance",    s:"balance",      i:"spell_nature_starfall.jpg"},
           {n:"Feral",      s:"feral",        i:"ability_druid_catform.jpg"},
           {n:"Guardian",   s:"guardian",     i:"ability_racial_bearform.jpg"},
           {n:"Restoration",s:"restoration",  i:"spell_nature_healingtouch.jpg"}] },
  { name:"Evoker",        slug:"evoker",          color:"#33937F", icon:"classicon_evoker.jpg",
    specs:[{n:"Devastation",s:"devastation",  i:"classicon_evoker_devastation.jpg"},
           {n:"Preservation",s:"preservation",i:"classicon_evoker_preservation.jpg"},
           {n:"Augmentation",s:"augmentation",i:"classicon_evoker_augmentation.jpg"}] },
  { name:"Hunter",        slug:"hunter",          color:"#AAD372", icon:"classicon_hunter.jpg",
    specs:[{n:"Beast Mastery",s:"beast-mastery",i:"ability_hunter_bestialdiscipline.jpg"},
           {n:"Marksmanship",s:"marksmanship",   i:"ability_hunter_focusedaim.jpg"},
           {n:"Survival",    s:"survival",       i:"ability_hunter_camouflage.jpg"}] },
  { name:"Mage",          slug:"mage",            color:"#3FC7EB", icon:"classicon_mage.jpg",
    specs:[{n:"Arcane",     s:"arcane",       i:"spell_holy_magicalsentry.jpg"},
           {n:"Fire",       s:"fire",         i:"spell_fire_firebolt02.jpg"},
           {n:"Frost",      s:"frost",        i:"spell_frost_frostbolt02.jpg"}] },
  { name:"Monk",          slug:"monk",            color:"#00FF98", icon:"classicon_monk.jpg",
    specs:[{n:"Brewmaster", s:"brewmaster",   i:"spell_monk_brewmaster_spec.jpg"},
           {n:"Windwalker", s:"windwalker",   i:"spell_monk_windwalker_spec.jpg"},
           {n:"Mistweaver", s:"mistweaver",   i:"spell_monk_mistweaver_spec.jpg"}] },
  { name:"Paladin",       slug:"paladin",         color:"#F48CBA", icon:"classicon_paladin.jpg",
    specs:[{n:"Holy",       s:"holy",         i:"spell_holy_holybolt.jpg"},
           {n:"Protection", s:"protection",   i:"ability_paladin_shieldofthetemplar.jpg"},
           {n:"Retribution",s:"retribution",  i:"spell_holy_auraoflight.jpg"}] },
  { name:"Priest",        slug:"priest",          color:"#E8E8E8", icon:"classicon_priest.jpg",
    specs:[{n:"Discipline", s:"discipline",   i:"spell_holy_powerwordshield.jpg"},
           {n:"Holy",       s:"holy",         i:"spell_holy_guardianspirit.jpg"},
           {n:"Shadow",     s:"shadow",       i:"spell_shadow_shadowwordpain.jpg"}] },
  { name:"Rogue",         slug:"rogue",           color:"#FFF468", icon:"classicon_rogue.jpg",
    specs:[{n:"Assassination",s:"assassination",i:"ability_rogue_eviscerate.jpg"},
           {n:"Outlaw",     s:"outlaw",       i:"ability_rogue_waylay.jpg"},
           {n:"Subtlety",   s:"subtlety",     i:"ability_stealth.jpg"}] },
  { name:"Shaman",        slug:"shaman",          color:"#0070DD", icon:"classicon_shaman.jpg",
    specs:[{n:"Elemental",  s:"elemental",    i:"spell_nature_lightning.jpg"},
           {n:"Enhancement",s:"enhancement",  i:"spell_shaman_improvedstormstrike.jpg"},
           {n:"Restoration",s:"restoration",  i:"spell_nature_magicimmunity.jpg"}] },
  { name:"Warlock",       slug:"warlock",         color:"#8788EE", icon:"classicon_warlock.jpg",
    specs:[{n:"Affliction", s:"affliction",   i:"spell_shadow_deathcoil.jpg"},
           {n:"Demonology", s:"demonology",   i:"spell_shadow_metamorphosis.jpg"},
           {n:"Destruction",s:"destruction",  i:"spell_shadow_rainoffire.jpg"}] },
  { name:"Warrior",       slug:"warrior",         color:"#C69B3A", icon:"classicon_warrior.jpg",
    specs:[{n:"Arms",       s:"arms",         i:"ability_warrior_savageblow.jpg"},
           {n:"Fury",       s:"fury",         i:"ability_warrior_innerrage.jpg"},
           {n:"Protection", s:"protection",   i:"ability_warrior_defensivestance.jpg"}] },
];

const GUIDE_MODES = [
  { id:"m+",           label:"M+" },
  { id:"3v3",          label:"3v3 Arena" },
  { id:"2v2",          label:"2v2 Arena" },
  { id:"solo-shuffle", label:"Solo Shuffle" },
];

function SpecPill({ cls, spec, mode }) {
  const [hv, setHv] = useState(false);
  const url = `https://murlok.io/${cls.slug}/${spec.s}/${mode}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6,
         padding:"5px 10px 5px 6px", borderRadius:30,
         border:`1px solid ${hv ? cls.color+"80" : "rgba(255,255,255,.07)"}`,
         background: hv ? cls.color+"18" : "rgba(255,255,255,.03)",
         transition:"all .18s ease", cursor:"pointer",
         boxShadow: hv ? `0 0 10px ${cls.color}22` : "none" }}
       onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>
      <img src={ICON+spec.i} alt={spec.n} width={20} height={20}
           style={{ borderRadius:4, display:"block", flexShrink:0 }} />
      <span style={{ fontSize:11, fontFamily:"'Rajdhani',sans-serif", fontWeight:700,
        letterSpacing:".06em", color: hv ? cls.color : "rgba(255,255,255,.65)",
        transition:"color .18s", whiteSpace:"nowrap" }}>
        {spec.n}
      </span>
    </a>
  );
}

function ClassRow({ cls, mode, index }) {
  const [hv, setHv] = useState(false);
  return (
    <div
      style={{ position:"relative", overflow:"hidden", borderRadius:18,
        border:`1px solid ${hv ? cls.color+"40" : "rgba(255,255,255,.05)"}`,
        background: hv ? `rgba(255,255,255,.03)` : "#111113",
        padding:"14px 16px", transition:"all .22s ease",
        boxShadow: hv ? `0 4px 24px ${cls.color}18` : "none",
        animation:`fadeUp .4s ${index*35}ms ease both`, opacity:0 }}
      onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>

      {/* Class art background */}
      <div style={{ position:"absolute", right:0, top:0, bottom:0, width:160,
        backgroundImage:`url(${ART+cls.slug}.webp)`,
        backgroundSize:"cover", backgroundPosition:"center",
        opacity: hv ? 0.12 : 0.05,
        transition:"opacity .3s ease",
        maskImage:"linear-gradient(to left, rgba(0,0,0,.8) 0%, transparent 100%)",
        WebkitMaskImage:"linear-gradient(to left, rgba(0,0,0,.8) 0%, transparent 100%)",
        pointerEvents:"none" }} />

      <div style={{ position:"relative", display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
        {/* Class icon + name */}
        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0, minWidth:160 }}>
          <img src={ICON+cls.icon} alt={cls.name} width={32} height={32}
               style={{ borderRadius:8, border:`1px solid ${cls.color}40`, flexShrink:0 }} />
          <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:800, fontSize:14,
            letterSpacing:".08em", textTransform:"uppercase",
            color: hv ? cls.color : "rgba(255,255,255,.75)",
            transition:"color .2s" }}>
            {cls.name}
          </span>
        </div>

        {/* Spec pills */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, flex:1 }}>
          {cls.specs.map(spec => (
            <SpecPill key={spec.s} cls={cls} spec={spec} mode={mode} />
          ))}
        </div>
      </div>
    </div>
  );
}

function GuidesSection() {
  const [mode, setMode] = useState("m+");
  return (
    <section id="guides" style={{ marginBottom:52 }}>
      {/* Header */}
      <div className="fu2" style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:38, height:38, borderRadius:12,
            background:"linear-gradient(135deg,rgba(255,128,0,.2),rgba(139,92,246,.2))",
            border:"1px solid rgba(255,255,255,.08)",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <img src={ICON+"inv_misc_book_11.jpg"} alt="" width={22} height={22}
                 style={{ borderRadius:4 }} />
          </div>
          <div>
            <div className="stt" style={{ fontSize:26, color:"#fff", lineHeight:1 }}>CLASS GUIDES</div>
            <div className="rj" style={{ fontSize:10, color:"var(--dim)", letterSpacing:".08em", fontWeight:600 }}>
              POWERED BY MURLOK.IO · MIDNIGHT SEASON 1
            </div>
          </div>
        </div>

        {/* Mode switcher */}
        <div style={{ display:"flex", gap:4, background:"#111113",
          border:"1px solid var(--border)", borderRadius:14, padding:4 }}>
          {GUIDE_MODES.map(m => (
            <button key={m.id}
              onClick={() => setMode(m.id)}
              style={{ padding:"6px 14px", borderRadius:10, border:"none", cursor:"pointer",
                fontFamily:"'Rajdhani',sans-serif", fontWeight:700, fontSize:11,
                letterSpacing:".07em", textTransform:"uppercase", transition:"all .18s",
                background: mode===m.id ? "rgba(255,255,255,.09)" : "transparent",
                color: mode===m.id ? "#fff" : "var(--muted)" }}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Class list */}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {CLASSES.map((cls, i) => (
          <ClassRow key={cls.slug} cls={cls} mode={mode} index={i} />
        ))}
      </div>

      {/* Footer link */}
      <div style={{ marginTop:16, textAlign:"center" }}>
        <a href="https://murlok.io" target="_blank" rel="noopener noreferrer"
           style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 22px",
             borderRadius:14, border:"1px solid rgba(255,255,255,.08)",
             background:"rgba(255,255,255,.03)", color:"rgba(255,255,255,.35)",
             fontSize:11, fontFamily:"'Rajdhani',sans-serif", fontWeight:700,
             letterSpacing:".08em", textTransform:"uppercase", textDecoration:"none",
             transition:"all .2s" }}
           onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,.06)";e.currentTarget.style.color="rgba(255,255,255,.7)"}}
           onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,.03)";e.currentTarget.style.color="rgba(255,255,255,.35)"}}>
          <ExternalLink size={12}/> View All Builds on Murlok.io
        </a>
      </div>
    </section>
  );
}

// ─── Background themes ────────────────────────────────────────────────────────
const BG_THEMES={
  all: {
    bg:"#0a0a0b",
    image:null,
    overlay:"linear-gradient(180deg,rgba(10,10,11,.45)0%,rgba(10,10,11,.75)60%,#0a0a0b 100%)",
    color:"#818cf8",
    label:null,
    // Particle palette: arcane purple/indigo
    particles:["#8866ff","#aa44cc","#6644aa","#5544dd","#9966ff","#cc44ff"],
    mist:["#6644aa","#4422aa","#8844cc"],
  },
  wow: {
    bg:"#07090f",
    image:"https://bnetcmsus-a.akamaihd.net/cms/gallery/D60OE6PJOOA91568925329129.jpg",
    overlay:"linear-gradient(180deg,rgba(7,9,15,.38)0%,rgba(7,9,15,.68)55%,rgba(7,9,15,.95)85%,#07090f 100%)",
    color:"#60a5fa",
    label:"WORLD OF WARCRAFT",
    // Particle palette: arcane blue + gold
    particles:["#4488ff","#2266dd","#88aaff","#ffcc44","#ffaa22","#aa88ff","#66bbff"],
    mist:["#1133aa","#2244cc","#334499"],
  },
  dota:{
    bg:"#0b0707",
    image:"https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/backgrounds/greyfull.jpg",
    overlay:"linear-gradient(180deg,rgba(11,7,7,.30)0%,rgba(11,7,7,.62)55%,rgba(11,7,7,.96)85%,#0b0707 100%)",
    color:"#f87171",
    label:"DOTA 2",
    // Particle palette: embers — orange/red/gold (like dota2.com/home)
    particles:["#ff4400","#ff6600","#ff8800","#ffaa00","#dd2200","#ff3300","#ff5500","#cc1100"],
    mist:["#6b0a0a","#8b1a0a","#4a0505"],
  },
  blue:{
    bg:"#060c14",
    image:"https://bnetcmsus-a.akamaihd.net/cms/gallery/2x/9WCLZ7QOMHKP1591320298347.jpg",
    overlay:"linear-gradient(180deg,rgba(6,12,20,.40)0%,rgba(6,12,20,.70)55%,rgba(6,12,20,.95)85%,#060c14 100%)",
    color:"#93c5fd",
    label:"BLUE POSTS",
    // Particle palette: icy blue
    particles:["#0066ff","#2288ff","#88aaff","#4499ff","#0044cc","#66ccff","#aaddff"],
    mist:["#002266","#003388","#001144"],
  },
};

// ─── Video Background ─────────────────────────────────────────────────────────
//
//  YouTube cinematic videos cycle every 90 s with a smooth crossfade.
//  Only active on the WoW and Dota 2 tabs — other tabs use the canvas particles.
//
//  HOW TO ADD MORE VIDEOS:
//  1. Open a YouTube cinematic you like
//  2. Copy the 11-character ID from the URL bar (e.g. youtube.com/watch?v=XXXXXXXXXXX)
//  3. Paste it into the array below for the right game
//

const VIDEO_PLAYLISTS = {
  wow: [
    // ✅ The War Within | "Shadows Beneath" — Official Cinematic (Jul 2024)
    "zYdFLUBjwCU",
    // ✅ The War Within | Announce Cinematic "Echoes of the World Soul" (BlizzCon 2023)
    "hc9lqT6ZmGw",
    // ✅ Dragonflight | Official Announce Cinematic
    "AQG5p8i_oj8",
    // ✅ WoW Shadowlands | Official Cinematic "Beyond the Veil"
    "TjGHNnsoRtg",
  ],
  dota: [
    // ✅ The International 2024 — Opening Ceremony cinematic
    "j_-1_s4hMrE",
    // ✅ Dota 2 — "Are You Ready?" Animated Trailer
    "R6BaxKyNi2M",
    // ✅ Dota 2 | Crownfall Act IV — Kez Reveal Cinematic
    "UuQ0IaRV7EQ",
    // ✅ Dota 2 — Official Gameplay Trailer
    "pX0sefmk5OA",
  ],
};

// Rotate interval in ms (90 seconds per video)
const VIDEO_ROTATE_MS = 90_000;

function VideoBackground({ tab, bgOpacity }) {
  const [vidIdx, setVidIdx]   = useState(0);
  const [visible, setVisible] = useState(true); // for crossfade

  const playlist = VIDEO_PLAYLISTS[tab] ?? [];

  // Reset to first video when tab changes
  useEffect(() => {
    setVidIdx(0);
    setVisible(true);
  }, [tab]);

  // Cycle videos every 90 seconds with 1 s crossfade
  useEffect(() => {
    if (playlist.length <= 1) return;
    const iv = setInterval(() => {
      setVisible(false); // fade out
      setTimeout(() => {
        setVidIdx(i => (i + 1) % playlist.length);
        setVisible(true); // fade in
      }, 900);
    }, VIDEO_ROTATE_MS);
    return () => clearInterval(iv);
  }, [tab, playlist.length]);

  if (!playlist.length) return null;

  const videoId = playlist[vidIdx];
  // YouTube embed params: autoplay, muted, looped, no controls/branding
  const src = [
    `https://www.youtube.com/embed/${videoId}`,
    `?autoplay=1&mute=1&loop=1&controls=0`,
    `&disablekb=1&modestbranding=1&rel=0`,
    `&showinfo=0&iv_load_policy=3&cc_load_policy=0`,
    `&playlist=${videoId}&playsinline=1`,
  ].join("");

  return (
    <div style={{
      position:  "fixed", inset: 0, zIndex: 2,
      overflow:  "hidden", pointerEvents: "none",
      opacity:   bgOpacity * (visible ? 1 : 0),
      transition:"opacity 0.9s ease",
    }}>
      <iframe
        key={videoId}
        src={src}
        frameBorder="0"
        allow="autoplay; fullscreen"
        style={{
          position:  "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          // Cover the viewport regardless of aspect ratio
          width:  "177.78vh",  /* 16:9 → 100vh × 16/9 */
          height: "56.25vw",   /* 9:16  → 100vw × 9/16 */
          minWidth:  "100%",
          minHeight: "100%",
          pointerEvents: "none",
          border: "none",
        }}
        title="background-video"
      />

      {/* Next video button — only clickable */}
      {playlist.length > 1 && (
        <button
          onClick={() => {
            setVisible(false);
            setTimeout(() => {
              setVidIdx(i => (i + 1) % playlist.length);
              setVisible(true);
            }, 400);
          }}
          style={{
            position: "fixed", bottom: 20, right: 20,
            zIndex: 9, pointerEvents: "all", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 10,
            border: "1px solid rgba(255,255,255,.15)",
            background: "rgba(0,0,0,.55)", backdropFilter: "blur(8px)",
            color: "rgba(255,255,255,.5)", fontSize: 10,
            fontFamily: "'Rajdhani',sans-serif", fontWeight: 700,
            letterSpacing: ".08em", textTransform: "uppercase",
            transition: "all .2s",
          }}
          onMouseEnter={e=>{e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="rgba(255,255,255,.35)"}}
          onMouseLeave={e=>{e.currentTarget.style.color="rgba(255,255,255,.5)";e.currentTarget.style.borderColor="rgba(255,255,255,.15)"}}
        >
          ▶ NEXT VIDEO ({vidIdx + 1}/{playlist.length})
        </button>
      )}
    </div>
  );
}

// ─── Animated Background (Canvas particle system) ─────────────────────────────
//
//  Inspired by dota2.com/home — dark atmosphere with glowing embers/sparks,
//  slow-drifting mist blobs, and subtle volumetric light rays.
//  Colors swap when the tab changes (dota = fire embers, wow = arcane sparks…)
//
function AnimatedBackground({ theme, tab, bgOpacity }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const stateRef  = useRef(null); // holds live mutable state so effect doesn't re-run

  // Resize canvas to fill screen
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      // Rebuild particles on resize
      if (stateRef.current) stateRef.current.needsRebuild = true;
    };
    resize();
    window.addEventListener("resize", resize, { passive: true });
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Main animation loop — restarts when tab changes to pick up new colors
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    cancelAnimationFrame(rafRef.current);

    const ctx = canvas.getContext("2d");
    const W   = () => canvas.width;
    const H   = () => canvas.height;

    const colors = theme.particles;
    const mists  = theme.mist;
    const rnd    = (a, b) => a + Math.random() * (b - a);
    const pick   = arr => arr[Math.floor(Math.random() * arr.length)];
    const hex2rgb = h => {
      const r = parseInt(h.slice(1,3),16);
      const g = parseInt(h.slice(3,5),16);
      const b = parseInt(h.slice(5,7),16);
      return [r,g,b];
    };

    // ── Particle ──────────────────────────────────────────────────────────────
    class Particle {
      constructor(scatter) {
        this.scatter = scatter;
        this.reset();
        if (scatter) this.y = rnd(0, H()); // spread initial particles
      }
      reset() {
        this.x    = rnd(0, W());
        this.y    = H() + rnd(0, 80);          // spawn below screen
        this.vx   = rnd(-0.35, 0.35);
        this.vy   = -rnd(0.15, 0.7);            // float upward
        this.r    = rnd(0.4, 2.2);
        this.a    = 0;
        this.maxA = rnd(0.15, 0.65);
        this.age  = 0;
        this.life = rnd(160, 420);
        this.col  = pick(colors);
        this.wx   = rnd(0, Math.PI * 2);        // wobble phase
        this.ws   = rnd(-0.025, 0.025);         // wobble speed
        // Type: 0 = ember (small+sharp), 1 = orb (large+soft)
        this.type = Math.random() < 0.15 ? 1 : 0;
        if (this.type === 1) { this.r = rnd(3, 8); this.maxA *= 0.4; }
      }
      step() {
        this.age++;
        this.wx += this.ws;
        this.x  += this.vx + Math.sin(this.wx) * 0.25;
        this.y  += this.vy;
        // Fade in quickly, then fade out over life
        const t = this.age / this.life;
        this.a  = this.maxA * Math.min(1, t * 8) * Math.max(0, 1 - t);
        if (this.y < -20 || this.age > this.life) this.reset();
      }
      draw() {
        if (this.a <= 0.005) return;
        const gR = this.type === 1 ? this.r * 6 : this.r * 5;
        const g  = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, gR);
        const [r,gr,b] = hex2rgb(this.col);
        g.addColorStop(0,   `rgba(${r},${gr},${b},${this.a})`);
        g.addColorStop(0.4, `rgba(${r},${gr},${b},${this.a * 0.4})`);
        g.addColorStop(1,   `rgba(${r},${gr},${b},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(this.x, this.y, gR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Mist blob ─────────────────────────────────────────────────────────────
    class Mist {
      constructor() { this.reset(); this.x = rnd(0, W()); this.y = rnd(0, H()); }
      reset() {
        this.x  = rnd(-200, W() + 200);
        this.y  = rnd(H() * 0.2, H());
        this.r  = rnd(180, 400);
        this.vx = rnd(-0.18, 0.18);
        this.vy = rnd(-0.08, -0.02);    // drift slowly upward
        this.a  = rnd(0.015, 0.045);
        this.col = pick(mists);
        this.life= rnd(500, 1200);
        this.age = 0;
      }
      step() {
        this.x  += this.vx;
        this.y  += this.vy;
        this.age++;
        if (this.y < -this.r || this.age > this.life) this.reset();
      }
      draw() {
        const [r,g,b] = hex2rgb(this.col);
        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.r);
        grad.addColorStop(0,   `rgba(${r},${g},${b},${this.a})`);
        grad.addColorStop(0.5, `rgba(${r},${g},${b},${this.a * 0.3})`);
        grad.addColorStop(1,   `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Light ray (rare streak) ────────────────────────────────────────────────
    class Ray {
      constructor() { this.reset(); this.t = rnd(0, 300); }
      reset() {
        this.x     = rnd(W() * 0.1, W() * 0.9);
        this.angle = rnd(-0.3, 0.3);   // near-vertical
        this.w     = rnd(20, 60);
        this.len   = rnd(H() * 0.3, H() * 0.7);
        this.maxA  = rnd(0.01, 0.025);
        this.t     = 0;
        this.life  = rnd(200, 500);
        this.col   = pick(colors);
      }
      step() {
        this.t++;
        if (this.t > this.life) this.reset();
      }
      draw() {
        const progress = this.t / this.life;
        const a = this.maxA * Math.sin(progress * Math.PI); // bell fade
        if (a <= 0.003) return;
        ctx.save();
        ctx.translate(this.x, 0);
        ctx.rotate(this.angle);
        const [r,g,b] = hex2rgb(this.col);
        const g2 = ctx.createLinearGradient(-this.w/2, 0, this.w/2, 0);
        g2.addColorStop(0,   `rgba(${r},${g},${b},0)`);
        g2.addColorStop(0.5, `rgba(${r},${g},${b},${a})`);
        g2.addColorStop(1,   `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = g2;
        const g3 = ctx.createLinearGradient(0, 0, 0, this.len);
        g3.addColorStop(0,   `rgba(${r},${g},${b},${a})`);
        g3.addColorStop(1,   `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = g3;
        // Draw tapered ray
        ctx.beginPath();
        ctx.moveTo(-this.w/2, 0);
        ctx.lineTo(this.w/2,  0);
        ctx.lineTo(2, this.len);
        ctx.lineTo(-2, this.len);
        ctx.closePath();
        ctx.globalAlpha = a;
        const rayGrad = ctx.createLinearGradient(-this.w/2, 0, this.w/2, 0);
        const [r2,g2c,b2] = hex2rgb(this.col);
        rayGrad.addColorStop(0,   `rgba(${r2},${g2c},${b2},0)`);
        rayGrad.addColorStop(0.5, `rgba(${r2},${g2c},${b2},1)`);
        rayGrad.addColorStop(1,   `rgba(${r2},${g2c},${b2},0)`);
        ctx.fillStyle = rayGrad;
        ctx.fill();
        ctx.restore();
      }
    }

    // Build scene
    const NPART = 110;
    const NMIST = 7;
    const NRAY  = 3;
    const parts = Array.from({ length: NPART }, (_, i) => new Particle(i < NPART));
    const fogs  = Array.from({ length: NMIST }, () => new Mist());
    const rays  = Array.from({ length: NRAY  }, () => new Ray());

    let frame = 0;

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      frame++;

      // Clear with slight trail effect for smoother look
      ctx.clearRect(0, 0, W(), H());

      // Draw back→front: mist → rays → particles
      fogs.forEach(f  => { f.step(); f.draw(); });
      // Rays only on dota tab (most dramatic)
      if (tab === "dota" || tab === "wow") {
        rays.forEach(r => { r.step(); r.draw(); });
      }
      parts.forEach(p => { p.step(); p.draw(); });
    };

    loop();

    return () => cancelAnimationFrame(rafRef.current);
  }, [tab]); // ← re-runs on tab change to swap colors

  return (
    <>
      {/* Solid base */}
      <div style={{ position:"fixed", inset:0, zIndex:0,
        background: theme.bg, transition:"background .5s ease",
        pointerEvents:"none" }} />

      {/* Canvas particles */}
      <canvas ref={canvasRef} style={{ position:"fixed", inset:0, zIndex:1,
        width:"100%", height:"100%", pointerEvents:"none",
        opacity: bgOpacity, transition:"opacity .45s ease" }} />

      {/* Video (wow/dota) OR static wallpaper (blue) */}
      {(tab === "wow" || tab === "dota") ? (
        <VideoBackground tab={tab} bgOpacity={bgOpacity} />
      ) : (theme.image && (
        <div key={tab} style={{ position:"fixed", inset:0, zIndex:2,
          backgroundImage:`url(${theme.image})`,
          backgroundSize:"cover", backgroundPosition:"center top",
          backgroundRepeat:"no-repeat",
          opacity: bgOpacity * 0.55,
          transition:"opacity .45s ease",
          pointerEvents:"none" }} />
      ))}

      {/* Dark overlay — gradient from transparent top → solid bottom */}
      <div style={{ position:"fixed", inset:0, zIndex:3,
        background: theme.overlay,
        opacity: bgOpacity, transition:"opacity .45s ease",
        pointerEvents:"none" }} />

      {/* Vignette */}
      <div style={{ position:"fixed", inset:0, zIndex:4,
        background:"radial-gradient(ellipse 130% 130% at 50% 50%,transparent 30%,rgba(0,0,0,.65)100%)",
        pointerEvents:"none" }} />

      {/* Watermark */}
      {theme.label && (
        <div style={{ position:"fixed", top:"50%", left:"50%",
          transform:"translate(-50%,-50%)", zIndex:5,
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize:"clamp(80px,18vw,200px)", letterSpacing:".12em",
          color:"transparent",
          WebkitTextStroke:`1px ${theme.color??"#fff"}`,
          opacity: bgOpacity * 0.03,
          whiteSpace:"nowrap", userSelect:"none", pointerEvents:"none",
          transition:"opacity .5s ease" }}>
          {theme.label}
        </div>
      )}
    </>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App(){
  const [scrolled,  setScrolled] = useState(false);
  const [tab,       setTab]      = useState("all");
  const [bgOpacity, setBgOpacity]= useState(1);

  useEffect(()=>{
    const fn=()=>setScrolled(window.scrollY>20);
    window.addEventListener("scroll",fn,{passive:true});
    return()=>window.removeEventListener("scroll",fn);
  },[]);

  const handleTabChange = useCallback((newTab)=>{
    if(newTab===tab) return;
    setBgOpacity(0);
    setTimeout(()=>{ setTab(newTab); setBgOpacity(1); }, 180);
  },[tab]);

  const theme = BG_THEMES[tab] ?? BG_THEMES.all;

  return(
    <>
      <style>{GLOBAL_CSS}</style>

      {/* ── Animated background ── */}
      <AnimatedBackground theme={theme} tab={tab} bgOpacity={bgOpacity} />

      {/* ── Content ── */}
      <div style={{ position:"relative", zIndex:10 }}>
        <Header scrolled={scrolled}/>
        <main style={{ maxWidth:900, margin:"0 auto", padding:"32px 20px 60px" }}>
          <NewsSection tab={tab} setTab={handleTabChange}/>
          <MetaRankings/>
          <DotaMeta/>
          <GuidesSection/>
          <CommunitySection/>
        </main>
        <footer style={{ borderTop:"1px solid var(--border)", padding:"24px 20px", textAlign:"center" }}>
          <div style={{ maxWidth:900, margin:"0 auto" }}>
            <div className="stt" style={{ fontSize:14, color:"var(--dim)", letterSpacing:".15em", marginBottom:6 }}>RUSE'S BAKERY</div>
            <p style={{ fontSize:11, color:"rgba(255,255,255,.12)", marginBottom:12 }}>Fan-made gaming news aggregator. Not affiliated with Blizzard Entertainment or Valve Corporation.</p>
            <div style={{ display:"flex", justifyContent:"center", gap:20, flexWrap:"wrap" }}>
              {[["Discord","#5865f2","https://discord.gg/3yKHBx3JZ"],["Wowhead","#ff8000","https://www.wowhead.com"],["MMO-Champion","#4da6ff","https://www.mmo-champion.com"],["Dota 2","#c23c2a","https://www.dota2.com"]].map(([l,c,u])=>(
                <a key={l} href={u} target="_blank" rel="noopener noreferrer"
                   style={{ fontSize:11, color:c+"88", textDecoration:"none",
                     fontFamily:"'Rajdhani',sans-serif", fontWeight:600, letterSpacing:".06em" }}>
                  {l.toUpperCase()}
                </a>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

